
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart, registerables } from 'chart.js';
import { IInsightData, ResponseType, InsightMetric, InsightItem, InsightTrend, InsightComparison } from '@/models/insight';
import { chartColors } from '@/services/configs';

// Register Chart.js components
Chart.register(...registerables);

@Component
export default class InsightChart extends Vue {
  @Prop({ required: true })
  public insight!: IInsightData;

  @Prop({ default: false })
  public compact!: boolean;

  @Prop({ default: false })
  public showDebug!: boolean;

  @Prop({ default: true })
  public responsive!: boolean;

  @Prop({ default: 300 })
  public height!: number;

  // Component state
  public chart: Chart | null = null;
  public isLoading: boolean = false;
  public chartErrorMessage: string = '';
  public canvasId: string = '';

  // =============================================================================
  // 🚨 EXPLICIT RESPONSE TYPE CHART MAPPING
  // =============================================================================

  /**
   * Deterministic chart type selection based on explicit response_type
   * NO automatic detection, NO smart defaults, NO pattern matching
   */
  public getChartTypeFromResponseType(): string {
    if (!this.insight.response_type) {
      return 'none'; // No chart without explicit type
    }

    const responseTypeChartMap: Record<ResponseType, string> = {
      'data_table': 'horizontal-bar',    // Table view or horizontal bar chart
      'single_number': 'gauge',          // Gauge or large metric display
      'key_metrics': 'dashboard',        // Dashboard-style metric cards
      'comparison': 'grouped-bar',       // Grouped bar chart (current vs baseline)

      'trend_data': 'line',              // Line chart with trend indicators
      'summary_text': 'none',            // No chart for text summary
      'auto': 'adaptive',                // Adaptive based on detected actual data type
      'list': 'none',                    // No chart for numbered list items
    };

    return responseTypeChartMap[this.insight.response_type] || 'none';
  }

  public getChartTypeColor(): string {
    const chartType = this.getChartTypeFromResponseType();
    if (chartType === 'none') return 'grey';
    if (this.hasTypeMismatch) return 'warning';
    return 'primary';
  }

  // =============================================================================
  // CHART RENDERING METHODS
  // =============================================================================

  get canvasRef(): string {
    return `insightChart_${this.canvasId}`;
  }

  public mounted(): void {
    this.canvasId = `insight_chart_${Math.random().toString(36).substr(2, 9)}`;
    this.$nextTick(() => {
      this.renderChart();
    });
  }

  public beforeDestroy(): void {
    this.destroyChart();
  }

  @Watch('insight', { deep: true })
  public onInsightChanged(): void {
    this.$nextTick(() => {
      this.renderChart();
    });
  }

  public async renderChart(): Promise<void> {
    if (!this.hasValidData || this.getChartTypeFromResponseType() === 'none') {
      return;
    }

    try {
      this.isLoading = true;
      this.chartErrorMessage = '';

      // Debug: Log the actual data structure
      console.log('InsightChart: Raw insight data:', this.insight.data);
      console.log('InsightChart: Response type:', this.insight.response_type);
      console.log('InsightChart: Chart type mapping:', this.getChartTypeFromResponseType());

      // Destroy existing chart
      this.destroyChart();

      await this.$nextTick();

      const canvas = this.$refs[this.canvasRef] as HTMLCanvasElement;
      if (!canvas) {
        throw new Error('Canvas element not found');
      }

      const chartConfig = this.createChartConfig();
      
      // Debug: Log chart configuration
      console.log('InsightChart: Chart config:', chartConfig);
      
      this.chart = new Chart(canvas, chartConfig);

      console.log(`InsightChart: Rendered ${this.getChartTypeFromResponseType()} chart for response_type: ${this.insight.response_type}`);

    } catch (error) {
      console.error('InsightChart: Error rendering chart:', error);
      this.chartErrorMessage = error instanceof Error ? error.message : 'Failed to render chart';
    } finally {
      this.isLoading = false;
    }
  }

  public destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  // =============================================================================
  // CHART CONFIGURATION FACTORY
  // =============================================================================

  public createChartConfig(): any {
    const chartType = this.getChartTypeFromResponseType();

    switch (chartType) {
      case 'horizontal-bar':
        return this.createHorizontalBarConfig();
      case 'gauge':
        return this.createGaugeConfig();
      case 'dashboard':
        return this.createDashboardConfig();
      case 'grouped-bar':
        return this.createGroupedBarConfig();
      case 'ranked-bar':
        return this.createRankedBarConfig();
      case 'line':
        return this.createLineConfig();
      case 'adaptive':
        return this.createAdaptiveConfig();
      default:
        throw new Error(`Unsupported chart type: ${chartType}`);
    }
  }

  // =============================================================================
  // CHART TYPE CONFIGURATIONS
  // =============================================================================

  public createHorizontalBarConfig(): any {
    const items = this.insight.data?.items || [];
    
    // Map actual API data structure to chart format
    const chartData = items.map(item => ({
      label: this.getItemLabel(item),
      value: this.getItemValue(item)
    }));
    
    return {
      type: 'bar',
      data: {
        labels: chartData.map(item => item.label),
        datasets: [{
          label: this.insight.category,
          data: chartData.map(item => item.value),
          backgroundColor: chartColors.slice(0, chartData.length),
          borderColor: chartColors.slice(0, chartData.length),
          borderWidth: 1,
        }]
      },
      options: {
        ...this.getBaseOptions(),
        indexAxis: 'y', // Horizontal bars
        plugins: {
          ...this.getBaseOptions().plugins,
          title: {
            display: true,
            text: this.insight.category,
            font: { size: 14, weight: 'bold' }
          }
        }
      }
    };
  }

  public createGaugeConfig(): any {
    // For single number, create a simple doughnut chart as gauge representation
    const value = this.insight.value || this.insight.data?.metrics?.[0]?.value || 0;
    const maxValue = value * 1.2; // Estimate max for gauge
    
    return {
      type: 'doughnut',
      data: {
        labels: ['Value', 'Remaining'],
        datasets: [{
          data: [value, maxValue - value],
          backgroundColor: [chartColors[0], '#e0e0e0'],
          borderWidth: 0,
        }]
      },
      options: {
        ...this.getBaseOptions(),
        rotation: -90,
        circumference: 180,
        plugins: {
          ...this.getBaseOptions().plugins,
          legend: { display: false },
          title: {
            display: true,
            text: `${this.insight.category}: ${this.formatValue(value)}`,
            font: { size: 14, weight: 'bold' }
          }
        }
      }
    };
  }

  public createDashboardConfig(): any {
    // For key metrics, create a multi-metric bar chart
    const metrics = this.insight.data?.metrics || [];
    
    return {
      type: 'bar',
      data: {
        labels: metrics.map(metric => metric.name),
        datasets: [{
          label: 'Metrics',
          data: metrics.map(metric => metric.value),
          backgroundColor: chartColors.slice(0, metrics.length),
          borderColor: chartColors.slice(0, metrics.length),
          borderWidth: 1,
        }]
      },
      options: {
        ...this.getBaseOptions(),
        plugins: {
          ...this.getBaseOptions().plugins,
          title: {
            display: true,
            text: `${this.insight.category} - Key Metrics`,
            font: { size: 14, weight: 'bold' }
          }
        }
      }
    };
  }

  public createGroupedBarConfig(): any {
    const comparisons = this.insight.data?.comparisons || [];
    
    return {
      type: 'bar',
      data: {
        labels: comparisons.map((comp, index) => comp.label || `Item ${index + 1}`),
        datasets: [
          {
            label: 'Current',
            data: comparisons.map(comp => comp.current),
            backgroundColor: chartColors[0],
            borderColor: chartColors[0],
            borderWidth: 1,
          },
          {
            label: 'Baseline',
            data: comparisons.map(comp => comp.baseline),
            backgroundColor: chartColors[1],
            borderColor: chartColors[1],
            borderWidth: 1,
          }
        ]
      },
      options: {
        ...this.getBaseOptions(),
        plugins: {
          ...this.getBaseOptions().plugins,
          title: {
            display: true,
            text: `${this.insight.category} - Comparison`,
            font: { size: 14, weight: 'bold' }
          }
        }
      }
    };
  }

  public createRankedBarConfig(): any {
    const items = this.insight.data?.items || [];
    const sortedItems = [...items].sort((a, b) => b.value - a.value);
    
    return {
      type: 'bar',
      data: {
        labels: sortedItems.map(item => item.name),
        datasets: [{
          label: 'Ranked Values',
          data: sortedItems.map(item => item.value),
          backgroundColor: sortedItems.map((_, index) => 
            index < 3 ? chartColors[index] : chartColors[3] // Top 3 get special colors
          ),
          borderWidth: 1,
        }]
      },
      options: {
        ...this.getBaseOptions(),
        indexAxis: 'y',
        plugins: {
          ...this.getBaseOptions().plugins,
          title: {
            display: true,
            text: `${this.insight.category} - Ranked`,
            font: { size: 14, weight: 'bold' }
          }
        }
      }
    };
  }

  public createLineConfig(): any {
    const trends = this.insight.data?.trends || [];
    
    return {
      type: 'line',
      data: {
        labels: trends.map(trend => trend.period),
        datasets: [{
          label: this.insight.category,
          data: trends.map(trend => trend.value),
          borderColor: chartColors[0],
          backgroundColor: chartColors[0] + '20',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
        }]
      },
      options: {
        ...this.getBaseOptions(),
        plugins: {
          ...this.getBaseOptions().plugins,
          title: {
            display: true,
            text: `${this.insight.category} - Trend`,
            font: { size: 14, weight: 'bold' }
          }
        },
        scales: {
          ...this.getBaseOptions().scales,
          x: {
            display: true,
            title: {
              display: true,
              text: 'Time Period'
            }
          }
        }
      }
    };
  }

  public createAdaptiveConfig(): any {
    // For auto response type, detect based on available data
    if (this.insight.data?.items) {
      return this.createHorizontalBarConfig();
    }
    if (this.insight.data?.metrics) {
      return this.createDashboardConfig();
    }
    if (this.insight.data?.comparisons) {
      return this.createGroupedBarConfig();
    }
    if (this.insight.data?.trends) {
      return this.createLineConfig();
    }
    if (this.insight.value !== undefined) {
      return this.createGaugeConfig();
    }
    
    throw new Error('Auto chart type: No suitable data found for visualization');
  }

  // =============================================================================
  // BASE CHART OPTIONS
  // =============================================================================

  public getBaseOptions(): any {
    return {
      responsive: this.responsive,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            boxWidth: 12,
            font: { size: 11 }
          }
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: '#f0f0f0'
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };
  }

  // =============================================================================
  // VALIDATION AND STATE HELPERS
  // =============================================================================

  get hasValidData(): boolean {
    if (!this.insight.data) return false;
    
    return !!(
      this.insight.data.items?.length ||
      this.insight.data.metrics?.length ||
      this.insight.data.trends?.length ||
      this.insight.data.comparisons?.length ||
      this.insight.value !== undefined
    );
  }

  get hasChartError(): boolean {
    return !!this.chartErrorMessage;
  }

  get hasTypeMismatch(): boolean {
    return !!this.insight.type_mismatch;
  }

  public getResponseTypeValidationColor(): string {
    if (this.hasTypeMismatch) return 'error';
    if (this.insight.response_type && this.insight.actual_type) return 'success';
    return 'warning';
  }

  public getResponseTypeValidationIcon(): string {
    if (this.hasTypeMismatch) return 'mdi-alert-circle';
    if (this.insight.response_type && this.insight.actual_type) return 'mdi-check-circle';
    return 'mdi-help-circle';
  }

  public getContainerClass(): string {
    const classes = [];
    
    if (this.compact) {
      classes.push('chart-container--compact');
    }
    
    if (this.hasChartError) {
      classes.push('chart-container--error');
    }
    
    return classes.join(' ');
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  public formatValue(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  }

  // =============================================================================
  // DATA MAPPING HELPERS FOR API RESPONSE STRUCTURE
  // =============================================================================

  /**
   * Extract label/name from item regardless of API field naming
   */
  public getItemLabel(item: any): string {
    // Handle different field naming conventions from API
    return item.name || 
           item.product_name || 
           item.label || 
           item.title || 
           item.description || 
           'Unknown';
  }

  /**
   * Extract numeric value from item regardless of API field naming
   */
  public getItemValue(item: any): number {
    // Handle different field naming conventions from API
    // For inventory data, prioritize meaningful metrics
    if (typeof item.value === 'number') return item.value;
    if (typeof item.quantity === 'number') return item.quantity;
    if (typeof item.inventory === 'number') return item.inventory;
    if (typeof item.stock_level === 'number') return item.stock_level;
    
    // NEW: Priority for updated backend column names
    if (typeof item['[sold_quantity_param]'] === 'number') return item['[sold_quantity_param]'];
    if (typeof item['[average_daily_units_sold_param]'] === 'number') return item['[average_daily_units_sold_param]'];
    if (typeof item.sold_quantity_param === 'number') return item.sold_quantity_param;
    if (typeof item.average_daily_units_sold_param === 'number') return item.average_daily_units_sold_param;
    
    // Legacy column names (fallback)
    if (typeof item.last_30_day_sales === 'number') return item.last_30_day_sales;
    if (typeof item.daily_sales_rate === 'number') return item.daily_sales_rate;
    
    if (typeof item.count === 'number') return item.count;
    if (typeof item.amount === 'number') return item.amount;
    
    // Default to 1 for items that exist but have no numeric value (like out of stock items)
    return 1;
  }
}
